const supportedTypes = new Set(['text', 'search', 'email', 'tel', 'url', 'password', 'number']);

/** 只增强可编辑文本控件，保留日期、文件、选择器及组件已有清空交互。 */
export function canClearInput(input) {
  return (input.tagName === 'TEXTAREA' || (input.tagName === 'INPUT' && supportedTypes.has(input.type)))
    && !input.matches(':disabled') && !input.readOnly
    && !input.closest('[data-input-clear="off"], .arco-select, .arco-picker')
    && input.getAttribute('role') !== 'combobox'
    && !input.parentElement?.querySelector('.uni-input-clear, .arco-input-clear-btn');
}

/** 走原生输入事件，让Vue模型、uni-input和金额校验同步更新。 */
export function clearInputValue(input) {
  if (!canClearInput(input)) return;
  const win = input.ownerDocument.defaultView;
  const prototype = input.tagName === 'TEXTAREA' ? win.HTMLTextAreaElement.prototype : win.HTMLInputElement.prototype;
  Object.getOwnPropertyDescriptor(prototype, 'value').set.call(input, '');
  input.dispatchEvent(new win.Event('input', { bubbles: true }));
  input.dispatchEvent(new win.Event('change', { bubbles: true }));
  input.focus({ preventScroll: true });
}

/** 独立浮层不改变表单DOM层级，避免破坏各页面布局及密码显示按钮。 */
export function installInputClear(app, doc = document) {
  const win = doc.defaultView;
  const entries = new Map();
  let frame = 0;
  let disposed = false;
  const schedule = () => {
    if (!frame && !disposed) frame = win.requestAnimationFrame(render);
  };
  const remove = (input, entry) => {
    entry.button.remove();
    input.classList.remove('kg-clearable-input');
    input.style.removeProperty('--kg-input-clear-padding');
    entries.delete(input);
  };
  function render() {
    frame = 0;
    for (const [input, entry] of entries) {
      if (!input.isConnected || !canClearInput(input)) remove(input, entry);
    }
    for (const input of doc.querySelectorAll('input, textarea')) {
      if (!canClearInput(input)) continue;
      let entry = entries.get(input);
      if (!entry) {
        const padding = Math.max(8, parseFloat(win.getComputedStyle(input).paddingRight) || 0) + (input.type === 'number' ? 18 : 0);
        const button = doc.createElement('button');
        button.type = 'button';
        button.className = 'kg-input-clear-button';
        button.title = '清空';
        button.textContent = '×';
        button.hidden = true;
        button.addEventListener('pointerdown', event => event.preventDefault());
        button.addEventListener('click', event => {
          event.preventDefault();
          event.stopPropagation();
          clearInputValue(input);
          schedule();
        });
        input.style.setProperty('--kg-input-clear-padding', `${padding + 28}px`);
        input.classList.add('kg-clearable-input');
        doc.body.append(button);
        entry = { button, padding };
        entries.set(input, entry);
      }
      const label = input.getAttribute('aria-label') || input.placeholder
        || input.closest('uni-input, uni-textarea')?.querySelector('.uni-input-placeholder, .uni-textarea-placeholder')?.textContent
        || '输入内容';
      const accessibleName = `清空${label}`;
      if (entry.button.getAttribute('aria-label') !== accessibleName) entry.button.setAttribute('aria-label', accessibleName);
      const rect = input.getBoundingClientRect();
      const x = rect.right - entry.padding - 26;
      const y = input.tagName === 'TEXTAREA' ? rect.top + 6 : rect.top + (rect.height - 24) / 2;
      // 不显示被滚动容器裁切、弹层遮挡或屏幕外的输入框按钮。
      entry.button.style.pointerEvents = 'none';
      const hit = doc.elementFromPoint(x + 12, y + 12);
      const visible = !!input.value && rect.width >= 60 && rect.height >= 16
        && x >= 0 && y >= 0 && y + 24 <= win.innerHeight
        && (hit === input || input.contains(hit));
      entry.button.style.pointerEvents = '';
      entry.button.hidden = !visible;
      if (visible) {
        entry.button.style.left = `${x}px`;
        entry.button.style.top = `${y}px`;
      }
    }
  }
  const observer = new win.MutationObserver(records => {
    if (records.some(record => !record.target.closest?.('.kg-input-clear-button'))) schedule();
  });
  observer.observe(doc.body, { subtree: true, childList: true, attributes: true,
    attributeFilter: ['disabled', 'readonly', 'type', 'hidden', 'class', 'style', 'value'] });
  const resizeObserver = new win.ResizeObserver(schedule);
  resizeObserver.observe(doc.body);
  const events = ['input', 'change', 'focusin', 'focusout', 'scroll', 'transitionend', 'animationend'];
  events.forEach(name => doc.addEventListener(name, schedule, true));
  win.addEventListener('resize', schedule);
  win.visualViewport?.addEventListener('resize', schedule);
  win.visualViewport?.addEventListener('scroll', schedule);
  // Vue以属性更新value时不会产生DOM mutation；更新钩子负责同步按钮。
  app.mixin({ mounted: schedule, updated: schedule });
  schedule();
  return () => {
    disposed = true;
    win.cancelAnimationFrame(frame);
    observer.disconnect();
    resizeObserver.disconnect();
    events.forEach(name => doc.removeEventListener(name, schedule, true));
    win.removeEventListener('resize', schedule);
    win.visualViewport?.removeEventListener('resize', schedule);
    win.visualViewport?.removeEventListener('scroll', schedule);
    for (const [input, entry] of entries) remove(input, entry);
  };
}
